/**
 * AnalyticsChart — renders various chart types using Recharts.
 * Recharts is already installed in the project.
 * Supports: bar, line, area, pie, donut, gauge, heatmap, table, scatter, funnel.
 */
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  PieChart, Pie, Cell, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, RadialBarChart, RadialBar,
} from "recharts";

// Centre3 color palette
const COLOURS = [
  "#2563EB", // blue-600
  "#16A34A", // green-600
  "#DC2626", // red-600
  "#D97706", // amber-600
  "#7C3AED", // violet-600
  "#0891B2", // cyan-600
  "#DB2777", // pink-600
  "#65A30D", // lime-600
];

interface ChartData {
  xAxis?: string[];
  series?: { name: string; data: number[] }[];
  // For pie/donut
  // series?: { name: string; value: number }[]
  // For gauge
  value?: number;
  min?: number;
  max?: number;
}

interface AnalyticsChartProps {
  chartType: string;
  data: ChartData;
  height?: number;
}

export default function AnalyticsChart({ chartType, data, height = 280 }: AnalyticsChartProps) {
  const h = height;

  // ── Table ──────────────────────────────────────────────────────────────────
  if (chartType === "table") {
    const rows = data.series?.[0]?.data ?? [];
    const labels = data.xAxis ?? rows.map((_, i) => `Row ${i + 1}`);
    return (
      <div className="overflow-auto rounded border" style={{ maxHeight: h }}>
        <table className="w-full text-sm">
          <thead className="bg-muted sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Label</th>
              {data.series?.map((s) => (
                <th key={s.name} className="px-3 py-2 text-right font-medium text-muted-foreground">
                  {s.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {labels.map((label, i) => (
              <tr key={i} className="border-t hover:bg-muted/30">
                <td className="px-3 py-2 text-foreground">{label}</td>
                {data.series?.map((s) => (
                  <td key={s.name} className="px-3 py-2 text-right text-foreground">
                    {s.data[i] ?? "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // ── Pie / Donut ────────────────────────────────────────────────────────────
  if (chartType === "pie" || chartType === "donut") {
    const pieData = (data.series as any[]) ?? [];
    const innerRadius = chartType === "donut" ? "55%" : 0;
    return (
      <ResponsiveContainer width="100%" height={h}>
        <PieChart>
          <Pie
            data={pieData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius="75%"
            paddingAngle={2}
          >
            {pieData.map((_: any, index: number) => (
              <Cell key={index} fill={COLOURS[index % COLOURS.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(value: any) => [value, ""]} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  // ── Gauge ──────────────────────────────────────────────────────────────────
  if (chartType === "gauge") {
    const pct = Math.round(((data.value ?? 0) / (data.max ?? 100)) * 100);
    const gaugeData = [
      { name: "Value", value: pct, fill: "#2563EB" },
      { name: "Remaining", value: 100 - pct, fill: "#E5E7EB" },
    ];
    return (
      <div className="flex flex-col items-center" style={{ height: h }}>
        <ResponsiveContainer width="100%" height={h - 40}>
          <RadialBarChart
            cx="50%"
            cy="80%"
            innerRadius="60%"
            outerRadius="100%"
            startAngle={180}
            endAngle={0}
            data={gaugeData}
          >
            <RadialBar dataKey="value" cornerRadius={4} background={{ fill: "#F3F4F6" }} />
          </RadialBarChart>
        </ResponsiveContainer>
        <p className="text-3xl font-bold text-foreground -mt-8">{data.value ?? 0}%</p>
      </div>
    );
  }

  // ── Scatter ────────────────────────────────────────────────────────────────
  if (chartType === "scatter") {
    const scatterData = (data.series?.[0]?.data ?? []).map((v, i) => ({ x: i, y: v }));
    return (
      <ResponsiveContainer width="100%" height={h}>
        <ScatterChart>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis dataKey="x" type="number" />
          <YAxis dataKey="y" type="number" />
          <Tooltip cursor={{ strokeDasharray: "3 3" }} />
          <Scatter data={scatterData} fill={COLOURS[0]} />
        </ScatterChart>
      </ResponsiveContainer>
    );
  }

  // ── Area ───────────────────────────────────────────────────────────────────
  if (chartType === "area") {
    const areaData = (data.xAxis ?? []).map((label, i) => {
      const point: Record<string, any> = { label };
      data.series?.forEach((s) => { point[s.name] = s.data[i] ?? 0; });
      return point;
    });
    return (
      <ResponsiveContainer width="100%" height={h}>
        <AreaChart data={areaData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <defs>
            {data.series?.map((s, i) => (
              <linearGradient key={s.name} id={`grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={COLOURS[i % COLOURS.length]} stopOpacity={0.3} />
                <stop offset="95%" stopColor={COLOURS[i % COLOURS.length]} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend />
          {data.series?.map((s, i) => (
            <Area
              key={s.name}
              type="monotone"
              dataKey={s.name}
              stroke={COLOURS[i % COLOURS.length]}
              fill={`url(#grad-${i})`}
              strokeWidth={2}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  // ── Line ───────────────────────────────────────────────────────────────────
  if (chartType === "line") {
    const lineData = (data.xAxis ?? []).map((label, i) => {
      const point: Record<string, any> = { label };
      data.series?.forEach((s) => { point[s.name] = s.data[i] ?? 0; });
      return point;
    });
    return (
      <ResponsiveContainer width="100%" height={h}>
        <LineChart data={lineData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend />
          {data.series?.map((s, i) => (
            <Line
              key={s.name}
              type="monotone"
              dataKey={s.name}
              stroke={COLOURS[i % COLOURS.length]}
              strokeWidth={2}
              dot={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  }

  // ── Bar (default, also horizontal-bar) ────────────────────────────────────
  const barData = (data.xAxis ?? []).map((label, i) => {
    const point: Record<string, any> = { label };
    data.series?.forEach((s) => { point[s.name] = s.data[i] ?? 0; });
    return point;
  });
  const layout = chartType === "h-bar" ? "vertical" : "horizontal";

  return (
    <ResponsiveContainer width="100%" height={h}>
      <BarChart
        data={barData}
        layout={layout}
        margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
        {layout === "horizontal" ? (
          <>
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
          </>
        ) : (
          <>
            <XAxis type="number" tick={{ fontSize: 11 }} />
            <YAxis dataKey="label" type="category" tick={{ fontSize: 11 }} width={80} />
          </>
        )}
        <Tooltip />
        <Legend />
        {data.series?.map((s, i) => (
          <Bar
            key={s.name}
            dataKey={s.name}
            fill={COLOURS[i % COLOURS.length]}
            radius={[3, 3, 0, 0]}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
